/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import cx from "classnames";
import React, { useCallback, useLayoutEffect, useRef } from "react";

import { usePlayerTranslation } from "../../../../../../shared/context/PlayerTranslationContext";
import { useSeekHandler } from "../../../../../../shared/hooks/useSeekHandler";
import { formatCurrentTime } from "../../../../../../shared/utils/time";
import { usePlayerController, usePlayerTimer } from "../../../../../BasePlayer";

import { AdvertisementMarks } from "./AdvertisementMarks";

import "./Timeline.scss";

interface TimelineProps {
  withAdvertisementMarks?: boolean;
}

export const Timeline = (props: TimelineProps) => {
  const { withAdvertisementMarks = false } = props;

  const {
    timer: { progress, isLive, duration, currentTime, isInfinityStream },
    timeForProgress,
  } = usePlayerTimer();
  const { player } = usePlayerController();

  const { t } = usePlayerTranslation();

  const wasPausedRef = useRef<boolean>(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const hoverTimeRef = useRef<HTMLSpanElement>(null);
  const lastMousePositionProgressRef = useRef<number>(0);
  const hoverOverAdvertisementMarkRef = useRef<boolean>(false);

  const allowScrubbing = !(duration == Infinity && !isLive);
  const timelineEnabled = currentTime && !isInfinityStream;

  const calculateProgress = (mouseX: number) => {
    if (!timelineRef.current) {
      return 0;
    }

    const rect = timelineRef.current.getBoundingClientRect();

    return Number(
      Math.min(Math.max(0, mouseX - rect.x), rect.width) / rect.width,
    );
  };

  const onAdvertisementMouseEnter = useCallback(() => {
    hoverOverAdvertisementMarkRef.current = true;
    hoverTimeRef.current?.classList.add("advertisement");

    if (hoverTimeRef.current) {
      hoverTimeRef.current.innerText = t("PLAYER_TOOLTIP_AD");
    }
  }, []);

  const onAdvertisementMouseLeave = useCallback(() => {
    hoverOverAdvertisementMarkRef.current = false;
    hoverTimeRef.current?.classList.remove("advertisement");
  }, []);

  const updateHoverTooltip = useCallback(
    (progress: number) => {
      if (hoverTimeRef.current && !hoverOverAdvertisementMarkRef.current) {
        let content: string;

        if (player.isLive()) {
          const liveWindow = player.liveWindow();
          const secondsBehind = liveWindow - progress * liveWindow;

          content =
            (secondsBehind < 1 ? "" : "-") + formatCurrentTime(secondsBehind);
        } else {
          content = formatCurrentTime(timeForProgress(progress));
        }

        hoverTimeRef.current.innerText = content;
      }
    },
    [player, timeForProgress],
  );

  const onStartScrubbing = useCallback(() => {
    if (allowScrubbing) {
      wasPausedRef.current = player.isPaused();
      player.pause();
    }
  }, [allowScrubbing, player]);

  const onEndScrubbing = useCallback(
    (position: number) => {
      if (allowScrubbing) {
        const progress = calculateProgress(position);

        player.currentTime(timeForProgress(progress));

        if (!wasPausedRef.current) {
          player.play();
        }
      }
    },
    [allowScrubbing, player, timeForProgress],
  );

  const onProgressUpdate = useCallback(
    (position: number, isScrubbing: boolean) => {
      if (timelineRef.current) {
        const progress = calculateProgress(position);

        lastMousePositionProgressRef.current = progress;
        updateHoverTooltip(progress);

        if (!isScrubbing) {
          const currentProgressPosition =
            Number(
              timelineRef.current.style.getPropertyValue("--progress-position"),
            ) || 0;

          if (currentProgressPosition > progress) {
            hoverTimeRef.current?.classList.add("past");
          } else {
            hoverTimeRef.current?.classList.remove("past");
          }
        }

        timelineRef.current.style.setProperty(
          "--hover-position",
          progress.toString(),
        );

        if (isScrubbing && allowScrubbing) {
          timelineRef.current.style.setProperty(
            "--progress-position",
            progress.toString(),
          );
        }
      }
    },
    [allowScrubbing, updateHoverTooltip],
  );

  const {
    onMouseMove,
    onMouseDown,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onContextMenu,
  } = useSeekHandler({
    onStartScrubbing,
    onEndScrubbing,
    onProgressUpdate,
  });

  useLayoutEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.style.setProperty(
        "--progress-position",
        progress.toString(),
      );
    }

    if (lastMousePositionProgressRef.current) {
      updateHoverTooltip(lastMousePositionProgressRef.current);
    }
  }, [currentTime, progress, updateHoverTooltip]);

  const timelineClasses = cx("PlayerTimeline", {
    ["enabled"]: timelineEnabled,
    ["with-scrubbing"]: allowScrubbing,
  });

  return (
    <div
      className={timelineClasses}
      ref={timelineRef}
      onMouseMove={onMouseMove}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onContextMenu={onContextMenu}
    >
      <div className="PlayerTimeline__ProgressArea__ThumbIndicator" />
      <div className="PlayerTimeline__ProgressArea">
        <span
          className="PlayerTimeline__ProgressArea__Hover"
          ref={hoverTimeRef}
        />

        {withAdvertisementMarks && (
          <AdvertisementMarks
            onMouseEnter={onAdvertisementMouseEnter}
            onMouseLeave={onAdvertisementMouseLeave}
          />
        )}
      </div>
    </div>
  );
};
